// src/index.ts
import { base64urlEncode } from "@phantom/base64url";
import bs58 from "bs58";
import { Algorithm } from "@phantom/sdk-types";
var IndexedDbStamper = class {
  // Optional for PKI, required for OIDC
  constructor(config = {}) {
    this.db = null;
    this.activeKeyPairRecord = null;
    this.pendingKeyPairRecord = null;
    this.algorithm = Algorithm.ed25519;
    // Use Ed25519 for maximum security and performance
    // The type of stamper, can be changed at any time
    this.type = "PKI";
    if (typeof window === "undefined" || !window.indexedDB) {
      throw new Error("IndexedDbStamper requires a browser environment with IndexedDB support");
    }
    this.dbName = config.dbName || "phantom-indexed-db-stamper";
    this.storeName = config.storeName || "crypto-keys";
    this.keyName = config.keyName || "signing-key";
    this.type = config.type || "PKI";
    this.idToken = config.idToken;
    this.salt = config.salt;
  }
  /**
   * Initialize the stamper by opening IndexedDB and retrieving or generating keys
   */
  async init() {
    await this.openDB();
    this.activeKeyPairRecord = await this.loadActiveKeyPairRecord();
    if (!this.activeKeyPairRecord) {
      this.activeKeyPairRecord = await this.generateAndStoreNewKeyPair("active");
    }
    this.pendingKeyPairRecord = await this.loadPendingKeyPairRecord();
    return this.activeKeyPairRecord.keyInfo;
  }
  /**
   * Get the public key information
   */
  getKeyInfo() {
    return this.activeKeyPairRecord?.keyInfo || null;
  }
  /**
   * Reset the key pair by generating a new one
   */
  async resetKeyPair() {
    await this.clearStoredKeys();
    this.activeKeyPairRecord = await this.generateAndStoreNewKeyPair("active");
    this.pendingKeyPairRecord = null;
    return this.activeKeyPairRecord.keyInfo;
  }
  /**
   * Create X-Phantom-Stamp header value using stored private key
   * @param params - Parameters object with data and optional type/options
   * @returns Complete X-Phantom-Stamp header value
   */
  async stamp(params) {
    const { data } = params;
    if (!this.activeKeyPairRecord) {
      throw new Error("Stamper not initialized. Call init() first.");
    }
    const dataBytes = new Uint8Array(data);
    const signature = await crypto.subtle.sign(
      {
        name: this.algorithm,
        hash: "SHA-256"
      },
      this.activeKeyPairRecord.keyPair.privateKey,
      dataBytes
    );
    const signatureBase64url = base64urlEncode(new Uint8Array(signature));
    const stampType = params.type || this.type;
    const idToken = params.type === "OIDC" ? params.idToken : this.idToken;
    const salt = params.type === "OIDC" ? params.salt : this.salt;
    const stampData = stampType === "PKI" ? {
      // Decode base58 public key to bytes, then encode as base64url (consistent with ApiKeyStamper)
      publicKey: base64urlEncode(bs58.decode(this.activeKeyPairRecord.keyInfo.publicKey)),
      signature: signatureBase64url,
      kind: "PKI",
      algorithm: this.algorithm
    } : {
      kind: "OIDC",
      idToken,
      publicKey: base64urlEncode(bs58.decode(this.activeKeyPairRecord.keyInfo.publicKey)),
      salt,
      algorithm: this.algorithm,
      signature: signatureBase64url
    };
    const stampJson = JSON.stringify(stampData);
    return base64urlEncode(stampJson);
  }
  /**
   * Clear all stored keys
   */
  async clear() {
    await this.clearStoredKeys();
    this.activeKeyPairRecord = null;
    this.pendingKeyPairRecord = null;
  }
  async clearStoredKeys() {
    if (!this.db) {
      await this.openDB();
    }
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const deleteActiveKeyPair = store.delete(`${this.keyName}-active`);
      const deletePendingKeyPair = store.delete(`${this.keyName}-pending`);
      let completed = 0;
      const total = 2;
      const checkComplete = () => {
        completed++;
        if (completed === total) {
          resolve();
        }
      };
      deleteActiveKeyPair.onsuccess = checkComplete;
      deletePendingKeyPair.onsuccess = checkComplete;
      deleteActiveKeyPair.onerror = () => reject(deleteActiveKeyPair.error);
      deletePendingKeyPair.onerror = () => reject(deletePendingKeyPair.error);
    });
  }
  async openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
    });
  }
  /**
   * Generate a new keypair for rotation without making it active
   */
  async rotateKeyPair() {
    if (!this.db) {
      await this.openDB();
    }
    this.pendingKeyPairRecord = await this.generateAndStoreNewKeyPair("pending");
    return this.pendingKeyPairRecord.keyInfo;
  }
  /**
   * Switch to the pending keypair, making it active and cleaning up the old one
   */
  async commitRotation(authenticatorId) {
    if (!this.pendingKeyPairRecord) {
      throw new Error("No pending keypair to commit");
    }
    if (this.activeKeyPairRecord) {
      await this.removeKeyPairRecord("active");
    }
    this.pendingKeyPairRecord.status = "active";
    this.pendingKeyPairRecord.authenticatorId = authenticatorId;
    this.pendingKeyPairRecord.keyInfo.authenticatorId = authenticatorId;
    this.activeKeyPairRecord = this.pendingKeyPairRecord;
    this.pendingKeyPairRecord = null;
    await this.storeKeyPairRecord(this.activeKeyPairRecord, "active");
    await this.removeKeyPairRecord("pending");
  }
  /**
   * Discard the pending keypair on rotation failure
   */
  async rollbackRotation() {
    if (!this.pendingKeyPairRecord) {
      return;
    }
    await this.removeKeyPairRecord("pending");
    this.pendingKeyPairRecord = null;
  }
  async generateAndStoreNewKeyPair(type) {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: "Ed25519"
      },
      false,
      // non-extractable - private key can never be exported
      ["sign", "verify"]
    );
    const rawPublicKeyBuffer = await crypto.subtle.exportKey("raw", keyPair.publicKey);
    const publicKeyBase58 = bs58.encode(new Uint8Array(rawPublicKeyBuffer));
    const keyIdBuffer = await crypto.subtle.digest("SHA-256", rawPublicKeyBuffer);
    const keyId = base64urlEncode(new Uint8Array(keyIdBuffer)).substring(0, 16);
    const now = Date.now();
    const keyInfo = {
      keyId,
      publicKey: publicKeyBase58,
      createdAt: now
    };
    const record = {
      keyPair,
      keyInfo,
      createdAt: now,
      expiresAt: 0,
      // Not used anymore, kept for backward compatibility
      status: type
    };
    await this.storeKeyPairRecord(record, type);
    return record;
  }
  async storeKeyPairRecord(record, type) {
    if (!this.db) {
      throw new Error("Database not initialized");
    }
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.put(record, `${this.keyName}-${type}`);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
  async loadActiveKeyPairRecord() {
    if (!this.db) {
      return null;
    }
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get(`${this.keyName}-active`);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }
  async loadPendingKeyPairRecord() {
    if (!this.db) {
      return null;
    }
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get(`${this.keyName}-pending`);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }
  async removeKeyPairRecord(type) {
    if (!this.db) {
      return;
    }
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(`${this.keyName}-${type}`);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
};
export {
  IndexedDbStamper
};
